<template>
  <div v-if="shutdownSettings" class="shutdown">
    <div class="shutdown__column">
      <div class="shutdown__column__contain">
        <span class="shutdown__column__contain__label">{{ t("shutdownIn") }}</span>

        <v-slider
          v-model="shutdownSettings.shutdownTimerInHours"
          :max="defaults.MAX_SHUTDOWN_TIME"
          :min="defaults.MIN_SHUTDOWN_TIME"
          :step="defaults.SHUTDOWN_STEP_VALUE"
          :tick-labels="tickLabels"
          :height="1"
          :disabled="disabled"
          rbt-target="uptimeValueSlider"
          class="shutdown__slider"
        />
      </div>

      <online-for :start="onlineForTime.start" :end="onlineForTime.end" :disabled="disabled" />

      <div v-if="isSaaS" class="shutdown__column__contain">
        <v-checkbox
          v-model="shutdownSettings.inactivityTrackerEnabled"
          :disabled="disabled"
          class="shutdown__input--checkbox"
          hide-details
        >
          <template v-slot:label>
            <div class="shutdown__role-checkbox">
              <div class="shutdown__role-checkbox__title">{{ t("checkbox.title") }}</div>

              <div class="shutdown__role-checkbox__description">
                {{ t("checkbox.description") }}
              </div>
            </div>
          </template>
        </v-checkbox>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "@vue/composition-api"
import { IOrganization, OrganizationType } from "@/types/organization"
import { createTranslationModule } from "@/plugins/i18n"
import { useStore } from "@/composable/useStore"
import OnlineFor from "../_OnlineFor.vue"
import Utils from "@/utils/utils"

const defaults = {
  MAX_SHUTDOWN_TIME: 12,
  MIN_SHUTDOWN_TIME: 1,
  SHUTDOWN_STEP_VALUE: 0.5
}

export default defineComponent({
  name: "TimerSettings",
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { spaceModule, organizationModule } = useStore()
    const t = createTranslationModule("AutoShutdown.timerSettings")

    const shutdownSettings = computed(() => {
      return spaceModule.scheduleModule.temp.shutdownSettings
    })

    const isSaaS = computed(() => {
      const organization = organizationModule.organizationDetail as IOrganization
      return organization?.organizationType === OrganizationType.SaaS
    })

    const shutdownLabel = computed(() => {
      const [h, min] = `${shutdownSettings.value?.shutdownTimerInHours}`.split(".").map(s => Number(s))
      return `${h}:${min ? "30" : "00"}`
    })

    const tickLabels = computed(() => {
      const tickCount = []

      for (let x = 1; x < defaults.MAX_SHUTDOWN_TIME + 1; x += 0.5) {
        const suffix = t("hours", {}, 1)

        if (x === parseInt(`${x}`)) {
          tickCount.push(x + suffix)
        } else {
          tickCount.push("")
        }
      }

      return tickCount
    })

    const onlineForTime = computed(() => {
      return {
        start: Date.now(),
        end: Date.now() + Utils.hours(shutdownSettings.value?.shutdownTimerInHours || 0)
      }
    })

    return {
      t,
      isSaaS,
      tickLabels,
      shutdownLabel,
      shutdownSettings,
      defaults,
      onlineForTime
    }
  },
  components: {
    OnlineFor
  }
})
</script>
